import React, { useState } from 'react';
import { X, PackagePlus, Store, Tag, DollarSign, FileText, Send, AlertCircle, ScanLine } from 'lucide-react';
import { PackageItem } from '../types';
import { BarcodeScannerModal } from './BarcodeScannerModal';

interface NewTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<PackageItem, 'id' | 'createdAt'>) => Promise<void> | void;
  suiteNumber: string;
  userId: string;
  userName?: string;
  userEmail?: string;
}

export const NewTrackingModal: React.FC<NewTrackingModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  suiteNumber,
  userId,
  userName,
  userEmail
}) => {
  const [store, setStore] = useState('');
  const [trackingIn, setTrackingIn] = useState('');
  const [description, setDescription] = useState('');
  const [declaredValue, setDeclaredValue] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!store.trim() || !trackingIn.trim() || !description.trim()) {
      setError('Por favor preencha os campos obrigatórios (Loja, Rastreio e Descrição).');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onSubmit({
        suiteNumber,
        userId,
        clientName: userName,
        clientEmail: userEmail,
        trackingIn: trackingIn.trim(),
        store: store.trim(),
        description: description.trim(),
        declaredValue: Number(declaredValue) || 0,
        weightKg: 0, // Admin will measure upon arrival
        weightLb: 0,
        status: 'A caminho',
        notes: notes.trim(),
        photoUrl: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&auto=format&fit=crop&q=80'
      });
      // Reset form
      setStore('');
      setTrackingIn('');
      setDescription('');
      setDeclaredValue('');
      setNotes('');
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao cadastrar novo rastreio.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-start p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl overflow-hidden border border-slate-100 my-4 sm:my-8">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-sky-600 to-blue-700 text-white p-5 sm:p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/10 rounded-2xl border border-white/20">
              <PackagePlus className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold">Cadastrar Novo Rastreio</h3>
              <p className="text-sky-100 text-xs sm:text-sm">Avise o depósito sobre uma compra a caminho</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2.5 p-3.5 bg-red-50 text-red-700 rounded-xl text-xs sm:text-sm border border-red-200">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Store / Site */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Loja / Site de Compra *
            </label>
            <div className="relative">
              <Store className="w-5 h-5 absolute left-3.5 top-3 text-slate-400" />
              <input
                type="text"
                value={store}
                onChange={(e) => setStore(e.target.value)}
                placeholder="Ex: Amazon JP, Rakuten, Mercari, Surugaya..."
                className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-medium text-slate-800"
                required
              />
            </div>
          </div>

          {/* Tracking In (Japan Domestic) */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                Código de Rastreio Doméstico (Japão) *
              </label>
              <button
                type="button"
                onClick={() => setIsScannerOpen(true)}
                className="text-xs font-bold text-sky-600 hover:text-sky-700 flex items-center gap-1 transition-colors bg-sky-50 hover:bg-sky-100 px-2 py-0.5 rounded-md border border-sky-200"
              >
                <ScanLine className="w-3.5 h-3.5" />
                <span>Escanear Câmera</span>
              </button>
            </div>
            <div className="relative">
              <Tag className="w-5 h-5 absolute left-3.5 top-3 text-slate-400" />
              <input
                type="text"
                value={trackingIn}
                onChange={(e) => setTrackingIn(e.target.value)}
                placeholder="Ex: Yamato 129384756, Japan Post..."
                className="w-full pl-11 pr-24 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-medium text-slate-800 font-mono uppercase"
                required
              />
              <button
                type="button"
                onClick={() => setIsScannerOpen(true)}
                className="absolute right-1.5 top-1.5 bottom-1.5 px-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 shadow-sm transition-all active:scale-95"
                title="Escanear código da etiqueta com a câmera"
              >
                <ScanLine className="w-3.5 h-3.5" />
                <span>Escanear</span>
              </button>
            </div>
            <span className="text-[11px] text-slate-400 mt-1 block">
              Fornecido pelo vendedor ou escaneado diretamente da etiqueta do pacote.
            </span>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Descrição dos Produtos *
            </label>
            <div className="relative">
              <FileText className="w-5 h-5 absolute left-3.5 top-3 text-slate-400" />
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex: 2x Action Figure Naruto, 1x Chá Matcha 200g"
                className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-medium text-slate-800"
                required
              />
            </div>
          </div>

          {/* Declared Value */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Valor Declarado Aproximado (Iene JP ¥)
            </label>
            <div className="relative">
              <DollarSign className="w-5 h-5 absolute left-3.5 top-3 text-slate-400" />
              <input
                type="number"
                min="0"
                step="100"
                value={declaredValue}
                onChange={(e) => setDeclaredValue(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Ex: 12500"
                className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-medium text-slate-800"
              />
            </div>
            <span className="text-[11px] text-slate-400 mt-1 block">
              Usado para fins de declaração alfandegária e seguro.
            </span>
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Observações Especiais (Opcional)
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Instruções adicionais, fragilidade ou detalhes..."
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-medium text-slate-800 resize-none"
            />
          </div>

          {/* Submit Action */}
          <div className="pt-3 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-slate-600 font-semibold text-sm hover:bg-slate-100 rounded-xl transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white font-bold text-sm rounded-xl shadow-md shadow-sky-500/20 transition-all disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Cadastrar Rastreio</span>
                </>
              )}
            </button>
          </div>

        </form>
      </div>

      <BarcodeScannerModal
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onScanSuccess={(scannedCode) => {
          setTrackingIn(scannedCode);
        }}
        title="Escanear Código de Rastreio"
        description="Aponte a câmera para o código de barras ou QR Code do comprovante ou etiqueta."
      />
    </div>
  );
};

export default NewTrackingModal;

